/**
 * MES REST API Service
 * Handles all communication with the MES backend endpoints
 */

import { serializeProcessParameterForApi } from '../utils/processParameterSerialize';
import {
  Timeline,
  TimelineBatch,
  BatchPassport,
  BatchListItem,
  Product,
  Recipe,
  RecipeIngredient,
  RecipeLinePayload,
  RecipeMaterialLines,
  RecipeParameter,
  RecipeParameterPayload,
  SemiFinishedProduct,
  Ingredient,
  IngredientCreatePayload,
  IngredientAlias,
  IngredientType,
  IngredientPrice,
  Machine,
  ProductMachineConfig,
  ProductionPhase,
  ProductionPhaseType,
  ProductionPhasePayload,
  ProductionPhaseMaterial,
  ProductionPhaseMaterialPayload,
  PhaseMaterialConsumptionRow,
  PhaseMaterialDemandRow,
  BatchPhaseMaterialDemandRow,
  CandidateLot,
  TransferAvailableLot,
  ProductionDayResult,
  MesSetting,
  ProcessParameter,
  BatchAdjustment,
  BatchAdjustmentInput,
  FlourLot,
  FlourAnalysis,
  Trial,
  TrialIngredient,
  TrialParameter,
  TrialSchedule,
  DashboardStatus,
  GanttData,
  MachineEfficiency,
  QualityTrend,
  DailyPlanEntry,
  DispatchOverridePayload,
  TimelineFilters,
  ProductFilters,
  TrialFilters,
  DateRange,
  InterWarehouseTransfer,
  OrderRequest,
  OrderRequestStatus,
  OrderRequestPayload,
  UnitType,
  ProductionUnit,
  RecipeQuantityBasis,
  TraceMaterialLot,
  LotForwardTrace,
  TraceProducedLot,
  FinishedGoodsStockOverview,
  BatchBackwardTrace,
  TraceLotNodeDetail,
  TraceBatchNodeDetail,
  TraceDispatchNodeDetail,
  TraceReceiptNodeDetail,
  TraceTransferNodeDetail,
  DispatchCustomer,
  DispatchNote,
  DispatchNotePayload,
  ProductionWorkOrder,
  ProductionWorkOrderStatus,
  ProductionWorkOrderPayload,
  ProductionWorkOrderBatch,
  BatchMaterialsResult,
  BatchMaterialPayload,
  BatchMaterialLotRow,
  IngredientCandidateLot,
  Warehouse,
  InventoryCount,
  InventoryCountDetail,
  InventoryCountLookups,
  CountableItem,
  DailyProductionReport,
} from '../types/mes';

export type DeviceMeasurementSummary = {
  count: number;
  has_valid: boolean;
  latest_measured_at: string | null;
};

export type LabosDeviceKey = 'farinograph' | 'extensograph' | 'amylograph';
export type LabosUredaj = 'farinograf' | 'ekstenzograf' | 'amilograf';
export type LabosNalogStatus = 'plan' | 'u_tijeku' | 'zavrseno' | 'otkazano';

export type LabosTrialOption = {
  trial_id: number;
  trial_code: string;
  trial_date: string;
  product_name?: string | null;
};

export type LabosTrialLink = {
  id: number;
  trial_id: number;
  trial_code: string;
  trial_date: string;
  product_name?: string | null;
  linked_at?: string;
  notes?: string | null;
};

export type LabosMeasurementListItem = {
  id: number;
  measured_at?: string | null;
  sample_name?: string | null;
  method?: string | null;
  operator?: string | null;
  is_blend?: boolean;
  is_valid?: boolean | null;
  sample_weight?: number | null;
  water_absorption?: number | null;
  material_moisture?: number | null;
  remarks_raw?: string | null;
  link_count: number;
  trial_links: LabosTrialLink[];
};

export type LabosBlendComponent = {
  lot_number?: string | null;
  supplier?: string | null;
  flour_type?: string | null;
  proportion_pct: number;
};

export type LabosMeasurementDetail = {
  device: LabosDeviceKey;
  measurement_type: string;
  measurement: Record<string, unknown>;
  results: Record<string, unknown>[];
  blend_components: LabosBlendComponent[];
  trial_links: LabosTrialLink[];
};

export type LabosScaleIngredientInput = {
  ingredient_id?: number | null;
  ingredient_name: string;
  ingredient_type?: string | null;
  baker_percentage: number | string;
};

export type LabosScaledRow = {
  ingredient_name: string;
  ingredient_type: string | null;
  mass_g: number;
  is_manual_water?: boolean;
};

export type LabosScalePreview = {
  scaled: LabosScaledRow[];
  total_mass_g: number;
  target_brasno_g?: number;
  flour_kg?: number;
  /** amilograf: fiksna voda prije korekcije za starter (mL) */
  water_default_ml?: number;
  /** amilograf: voda iz startera (g) */
  starter_water_g?: number;
  /** amilograf: voda za ručno dodati (g) */
  water_to_add_g?: number;
  /** ekstenzograf: dodana sol (g) */
  salt_g?: number;
  /** farinograf: voda ovisi o metodi */
  water_by_method?: boolean;
};

export type LabosNalogIngredient = {
  id?: number;
  ingredient_name: string;
  ingredient_type?: string | null;
  mass_g: number;
  sort_order?: number;
};

export type LabosNalog = {
  nalog_id: number;
  nalog_code: string;
  uredaj: LabosUredaj;
  status: LabosNalogStatus;
  planned_date: string;
  trial_id?: number | null;
  measurement_id?: number | null;
  target_brasno_g: number;
  sample_name?: string | null;
  operator?: string | null;
  notes?: string | null;
  trial_code?: string | null;
  trial_product?: string | null;
  ingredients?: LabosNalogIngredient[];
};

export type CreateLabosNalogPayload = {
  uredaj: LabosUredaj;
  planned_date: string;
  sample_name: string;
  operator?: string;
  notes?: string;
  trial_id?: number | null;
  ingredients: LabosScaledRow[];
  created_by?: string;
};

export type FlourMeasurementSet = {
  farinograph: DeviceMeasurementSummary;
  extensograph: DeviceMeasurementSummary;
  amylograph: DeviceMeasurementSummary;
};

export type FlourQualityQueueItem = {
  lot_id: number;
  internal_lot_code?: string | null;
  lot_number_supplier?: string | null;
  arrival_date: string;
  quantity_received_kg: number;
  ingredient_name?: string;
  ingredient_type?: string | null;
  is_flour?: boolean;
  supplier_name?: string | null;
  delivery_note_number?: string;
  received_date?: string;
  blend_id: number | null;
  measurements: FlourMeasurementSet | null;
};

export type FlourTestCatalogEntry = {
  device: 'farinograph' | 'extensograph' | 'amylograph';
  device_label: string;
  measurement_id: number;
  measured_at: string | null;
  is_valid: boolean | null;
  sample_weight: number | null;
  method: string | null;
  operator: string | null;
  device_sn: string | null;
  sample_name: string | null;
  result_count: number;
  source_file: string | null;
};

export type FlourLotAnalysis = {
  lot: any;
  blend: any;
  measurements: FlourMeasurementSet;
  devices: {
    farinograph: any[];
    extensograph: any[];
    amylograph: any[];
  };
  all_tests: FlourTestCatalogEntry[];
  qc_decisions: any[];
  delivery_receipts: any[];
  certificates: any[];
};

export type FlourTestHistoryItem = {
  lot_id: number;
  internal_lot_code?: string | null;
  lot_number_supplier?: string | null;
  ingredient_name?: string;
  supplier_name?: string | null;
  status: string;
  arrival_date?: string;
  released_at?: string | null;
  released_by?: string | null;
  quantity_received_kg: number;
  blend_id: number | null;
  measurements: FlourMeasurementSet | null;
  last_qc: {
    decision: string;
    decided_at: string;
    decided_by: string;
    notes?: string | null;
    evidence_blend_id?: number | null;
  } | null;
};

class MESApi {
  private baseUrl: string;

  constructor() {
    this.baseUrl = (process.env.REACT_APP_SOCKET_URL || 'http://localhost:5150') + '/api/mes';
  }

  private getToken(): string {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const user = JSON.parse(userStr);
      return user.token || '';
    }
    return '';
  }

  private async request<T>(method: string, path: string, data?: any): Promise<T> {
    const token = this.getToken();

    const options: RequestInit = {
      method,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    };

    if (data && ['POST', 'PUT', 'PATCH'].includes(method)) {
      options.body = JSON.stringify(data);
    }

    const response = await fetch(`${this.baseUrl}${path}`, options);
    const json = await response.json();

    if (!response.ok || json.status >= 400) {
      throw new Error(json.error || json.message || 'Request failed');
    }

    return json.data;
  }

  // ==================== PRODUCTS ====================
  async getProducts(filters?: ProductFilters): Promise<Product[]> {
    const params = new URLSearchParams();
    if (filters?.is_active !== undefined) params.append('is_active', String(filters.is_active));
    if (filters?.category) params.append('category', filters.category);
    if (filters?.search) params.append('search', filters.search);
    if (filters?.with_phases) params.append('with_phases', 'true');
    if (filters?.production_unit) params.append('production_unit', String(filters.production_unit));

    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request<Product[]>('GET', `/products${query}`);
  }

  async getProduct(id: number): Promise<Product> {
    return this.request<Product>('GET', `/products/${id}`);
  }

  async createProduct(data: Partial<Product>): Promise<Product> {
    return this.request<Product>('POST', '/products', data);
  }

  async updateProduct(id: number, data: Partial<Product>): Promise<Product> {
    return this.request<Product>('PUT', `/products/${id}`, data);
  }

  // ==================== RECIPES ====================
  /**
   * Recipe list. By default the lines come back as ids and baker percentages only
   * — enough for counts, sums and dropdowns. Pass `with_ingredients` when you need
   * a name per line (ingredient or semi-finished); to render or edit a whole
   * recipe, fetch that one recipe with `getRecipe` instead of asking the list for
   * the full tree.
   */
  async getRecipes(filters?: { is_active?: boolean; product_id?: number; with_ingredients?: boolean }): Promise<Recipe[]> {
    const params = new URLSearchParams();
    if (filters?.is_active !== undefined) params.append('is_active', String(filters.is_active));
    if (filters?.product_id) params.append('product_id', String(filters.product_id));
    if (filters?.with_ingredients) params.append('with_ingredients', 'true');

    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request<Recipe[]>('GET', `/recipes${query}`);
  }

  async getRecipe(id: number): Promise<Recipe> {
    return this.request<Recipe>('GET', `/recipes/${id}`);
  }

  /**
   * Material lines of several recipes in one request, for the screens that scale
   * a recipe to a batch mass. Prefer this over `getRecipe` per recipe: the full
   * payload walks five levels of relations (mixer parameters, semi-finished
   * production phases) that those screens never read, and a day's plan needs one
   * recipe per product.
   */
  async getRecipeMaterialLines(recipeIds: number[]): Promise<RecipeMaterialLines[]> {
    if (recipeIds.length === 0) return [];
    return this.request<RecipeMaterialLines[]>(
      'GET',
      `/recipes/material-lines?ids=${recipeIds.join(',')}`
    );
  }

  async updateRecipeIngredients(id: number, ingredients: RecipeLinePayload[]): Promise<Recipe> {
    return this.request<Recipe>('PUT', `/recipes/${id}/ingredients`, { ingredients });
  }

  /** Always resolves to the full canonical set, defaults included. */
  async getRecipeParameters(id: number): Promise<RecipeParameter[]> {
    return this.request<RecipeParameter[]>('GET', `/recipes/${id}/parameters`);
  }

  /**
   * Saved in place on this recipe version, unlike ingredients — editing setpoints
   * does not publish a new version.
   */
  async updateRecipeParameters(id: number, parameters: RecipeParameterPayload[]): Promise<RecipeParameter[]> {
    return this.request<RecipeParameter[]>('PUT', `/recipes/${id}/parameters`, { parameters });
  }

  // ==================== SEMI-FINISHED ====================
  async getSemiFinishedProducts(filters?: { is_active?: boolean; production_unit?: number }): Promise<SemiFinishedProduct[]> {
    const params = new URLSearchParams();
    if (filters?.is_active !== undefined) params.append('is_active', String(filters.is_active));
    if (filters?.production_unit) params.append('production_unit', String(filters.production_unit));
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request<SemiFinishedProduct[]>('GET', `/semi-finished${query}`);
  }

  async getSemiFinishedProduct(id: number): Promise<SemiFinishedProduct> {
    return this.request<SemiFinishedProduct>('GET', `/semi-finished/${id}`);
  }

  async createSemiFinishedProduct(
    data: Partial<SemiFinishedProduct>
  ): Promise<SemiFinishedProduct> {
    return this.request<SemiFinishedProduct>('POST', '/semi-finished', data);
  }

  async updateSemiFinishedProduct(
    id: number,
    data: Partial<SemiFinishedProduct>
  ): Promise<SemiFinishedProduct> {
    return this.request<SemiFinishedProduct>('PUT', `/semi-finished/${id}`, data);
  }

  async updateSemiFinishedRecipeIngredients(
    id: number,
    ingredients: RecipeLinePayload[]
  ): Promise<SemiFinishedProduct> {
    return this.request<SemiFinishedProduct>('PUT', `/semi-finished/${id}/recipe/ingredients`, { ingredients });
  }

  // ==================== INGREDIENTS ====================
  async getIngredientTypes(): Promise<IngredientType[]> {
    return this.request<IngredientType[]>('GET', '/ingredient-types');
  }

  async getIngredients(filters?: {
    is_active?: boolean;
    ingredient_type?: string;
    raw_only?: boolean;
  }): Promise<Ingredient[]> {
    const params = new URLSearchParams();
    if (filters?.is_active !== undefined) params.append('is_active', String(filters.is_active));
    if (filters?.ingredient_type) params.append('ingredient_type', filters.ingredient_type);
    if (filters?.raw_only === false) params.append('raw_only', 'false');

    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request<Ingredient[]>('GET', `/ingredients${query}`);
  }

  async createIngredient(data: IngredientCreatePayload): Promise<Ingredient> {
    return this.request<Ingredient>('POST', '/ingredients', data);
  }

  /** Replaces the whole set of Ritam aliases linked to a sirovina. */
  async setIngredientAliases(id: number, aliasIds: number[]): Promise<Ingredient> {
    return this.request<Ingredient>('PUT', `/ingredients/${id}/aliases`, { AliasIDs: aliasIds });
  }

  async getIngredientAliases(filters?: { source_system?: string }): Promise<IngredientAlias[]> {
    const params = new URLSearchParams();
    if (filters?.source_system) params.append('source_system', filters.source_system);
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request<IngredientAlias[]>('GET', `/ingredient-aliases${query}`);
  }

  async getIngredientPrices(id: number): Promise<IngredientPrice[]> {
    return this.request<IngredientPrice[]>('GET', `/ingredients/${id}/prices`);
  }

  async addIngredientPrice(data: Partial<IngredientPrice>): Promise<IngredientPrice> {
    return this.request<IngredientPrice>('POST', '/ingredient-prices', data);
  }

  async createIngredientPrice(data: { Ingredient: number; PricePerKg: number; ValidFrom: string }): Promise<IngredientPrice> {
    return this.request<IngredientPrice>('POST', '/ingredient-prices', data);
  }

  // ==================== MACHINES ====================
  async getMachines(filters?: { machine_type?: string }): Promise<Machine[]> {
    const params = new URLSearchParams();
    if (filters?.machine_type) params.append('machine_type', filters.machine_type);
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request<Machine[]>('GET', `/machines${query}`);
  }

  async createMachine(data: Partial<Machine>): Promise<Machine> {
    return this.request<Machine>('POST', '/machines', data);
  }

  async updateMachine(id: number, data: Partial<Machine>): Promise<Machine> {
    return this.request<Machine>('PUT', `/machines/${id}`, data);
  }

  /** Non-host devices available to link a machine to (Diosna, Escher, PLCs…). */
  async getLinkableDevices(): Promise<{ ID: number; Name: string; DeviceType?: number; Status?: number }[]> {
    return this.request('GET', '/machines/linkable-devices');
  }

  /** Active product machine config (default workers per product/machine). */
  async getProductMachineConfig(filters?: { product_id?: number; machine_id?: number }): Promise<ProductMachineConfig[]> {
    const params = new URLSearchParams();
    if (filters?.product_id) params.append('product_id', String(filters.product_id));
    if (filters?.machine_id) params.append('machine_id', String(filters.machine_id));
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request<ProductMachineConfig[]>('GET', `/product-machine-config${query}`);
  }

  // ==================== PRODUCTION PHASES ====================
  async getProductionPhaseTypes(): Promise<ProductionPhaseType[]> {
    return this.request<ProductionPhaseType[]>('GET', '/production-phase-types');
  }

  async getProductPhases(productId: number): Promise<ProductionPhase[]> {
    return this.request<ProductionPhase[]>('GET', `/products/${productId}/phases`);
  }

  async setProductPhases(productId: number, phases: ProductionPhasePayload[]): Promise<ProductionPhase[]> {
    return this.request<ProductionPhase[]>('PUT', `/products/${productId}/phases`, { phases });
  }

  async getSemiFinishedPhases(semiFinishedId: number): Promise<ProductionPhase[]> {
    return this.request<ProductionPhase[]>('GET', `/semi-finished/${semiFinishedId}/phases`);
  }

  async setSemiFinishedPhases(semiFinishedId: number, phases: ProductionPhasePayload[]): Promise<ProductionPhase[]> {
    return this.request<ProductionPhase[]>('PUT', `/semi-finished/${semiFinishedId}/phases`, { phases });
  }

  async createProductionPhase(data: ProductionPhasePayload & { product_id?: number; semi_finished_id?: number }): Promise<ProductionPhase> {
    return this.request<ProductionPhase>('POST', '/production-phases', data);
  }

  async updateProductionPhase(id: number, data: ProductionPhasePayload): Promise<ProductionPhase> {
    return this.request<ProductionPhase>('PUT', `/production-phases/${id}`, data);
  }

  async deleteProductionPhase(id: number): Promise<{ id: number; deleted: boolean }> {
    return this.request<{ id: number; deleted: boolean }>('DELETE', `/production-phases/${id}`);
  }

  // ==================== PHASE MATERIALS (dodatne sirovine po fazi) ====================
  async getProductPhaseMaterials(productId: number): Promise<ProductionPhaseMaterial[]> {
    return this.request<ProductionPhaseMaterial[]>('GET', `/products/${productId}/phase-materials`);
  }

  async createPhaseMaterial(data: ProductionPhaseMaterialPayload): Promise<ProductionPhaseMaterial> {
    return this.request<ProductionPhaseMaterial>('POST', '/phase-materials', data);
  }

  async updatePhaseMaterial(id: number, data: Partial<ProductionPhaseMaterialPayload>): Promise<ProductionPhaseMaterial> {
    return this.request<ProductionPhaseMaterial>('PUT', `/phase-materials/${id}`, data);
  }

  async deletePhaseMaterial(id: number): Promise<{ status: number }> {
    return this.request<{ status: number }>('DELETE', `/phase-materials/${id}`);
  }

  // --- Izvršenje: prijedlog (FIFO) -> potvrda faznih materijala po šarži ---
  async getBatchPhaseMaterialConsumption(batchId: number): Promise<PhaseMaterialConsumptionRow[]> {
    return this.request<PhaseMaterialConsumptionRow[]>('GET', `/batches/${batchId}/phase-material-consumption`);
  }

  async getBatchPhaseMaterialDemands(batchId: number): Promise<PhaseMaterialDemandRow[]> {
    return this.request<PhaseMaterialDemandRow[]>('GET', `/batches/${batchId}/phase-material-demands`);
  }

  /** Potrebe za više šarži odjednom — dodjela lotova u planiranju gleda cijeli dan. */
  async getPhaseMaterialDemandsForBatches(batchIds: number[]): Promise<BatchPhaseMaterialDemandRow[]> {
    if (batchIds.length === 0) return [];
    return this.request<BatchPhaseMaterialDemandRow[]>(
      'GET',
      `/phase-material-demands?batch_ids=${batchIds.join(',')}`
    );
  }

  async getCandidateLots(aliasId: number, warehouseCode?: string): Promise<CandidateLot[]> {
    const qs = warehouseCode ? `?warehouseCode=${encodeURIComponent(warehouseCode)}` : '';
    return this.request<CandidateLot[]>('GET', `/ingredient-aliases/${aliasId}/candidate-lots${qs}`);
  }

  async proposePhaseMaterials(batchId: number, force?: boolean): Promise<unknown> {
    return this.request('POST', `/batches/${batchId}/propose-phase-materials`, { force: force === true });
  }

  async swapPhaseMaterialLot(consumptionId: number, materialLotId: number): Promise<unknown> {
    return this.request('PUT', `/phase-material-consumption/${consumptionId}/lot`, { materialLotId });
  }

  async confirmPhaseMaterials(
    batchId: number,
    opts?: { phaseCodes?: string[]; confirmedBy?: string },
  ): Promise<{ batchId: number; confirmed: number }> {
    return this.request('POST', `/batches/${batchId}/confirm-phase-materials`, {
      phaseCodes: opts?.phaseCodes,
      confirmedBy: opts?.confirmedBy,
    });
  }

  // ==================== SIROVINE ZAMJESA (recept + poluproizvodi) ====================
  /** Sirovine zamjesa: evidentirani lotovi + recepturne stavke bez lota. */
  async getBatchMaterials(batchId: number): Promise<BatchMaterialsResult> {
    return this.request<BatchMaterialsResult>('GET', `/batches/${batchId}/materials`);
  }

  /** FEFO prijedlog lota za recepturne stavke bez evidencije (bez knjiženja). */
  async proposeBatchMaterials(
    batchId: number,
  ): Promise<{ batchId: number; created: number; shortages: { material: string; requiredQty: number }[] }> {
    return this.request('POST', `/batches/${batchId}/propose-materials`, {});
  }

  /** Potvrda utroška — knjiži izdatak sa skladišta proizvodnje. */
  async confirmBatchMaterials(
    batchId: number,
    opts?: { consumptionIds?: number[]; confirmedBy?: string },
  ): Promise<{ batchId: number; confirmed: number }> {
    return this.request('POST', `/batches/${batchId}/confirm-materials`, {
      consumptionIds: opts?.consumptionIds,
      confirmedBy: opts?.confirmedBy,
    });
  }

  async addBatchMaterial(batchId: number, data: BatchMaterialPayload): Promise<unknown> {
    return this.request('POST', `/batches/${batchId}/materials`, data);
  }

  async updateBatchMaterial(consumptionId: number, data: BatchMaterialPayload): Promise<unknown> {
    return this.request('PUT', `/batch-materials/${consumptionId}`, data);
  }

  async deleteBatchMaterial(consumptionId: number): Promise<unknown> {
    return this.request('DELETE', `/batch-materials/${consumptionId}`);
  }

  /** Poništenje potvrde (storno izdatka) — ispravak nakon potvrde. */
  async unconfirmBatchMaterial(consumptionId: number, performedBy?: string): Promise<unknown> {
    return this.request('POST', `/batch-materials/${consumptionId}/unconfirm`, { performedBy });
  }

  async getIngredientCandidateLots(
    ingredientId: number,
    warehouseCode?: string,
  ): Promise<IngredientCandidateLot[]> {
    const qs = warehouseCode ? `?warehouseCode=${encodeURIComponent(warehouseCode)}` : '';
    return this.request<IngredientCandidateLot[]>('GET', `/ingredients/${ingredientId}/candidate-lots${qs}`);
  }

  /**
   * Dodijeljeni lotovi za više zamjesa u jednom pozivu — ispis radnog naloga za
   * miksanje treba lot svake sirovine svake zamjese na stranici.
   */
  async getBatchMaterialLots(
    batchIds: number[],
    opts?: { includePhaseMaterials?: boolean }
  ): Promise<BatchMaterialLotRow[]> {
    if (batchIds.length === 0) return [];
    const phase = opts?.includePhaseMaterials ? '&include_phase_materials=true' : '';
    return this.request<BatchMaterialLotRow[]>('GET', `/batch-materials?batch_ids=${batchIds.join(',')}${phase}`);
  }

  // ==================== TIMELINES ====================
  async getTimelines(filters?: TimelineFilters): Promise<Timeline[]> {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.fromDate) params.append('fromDate', filters.fromDate);
    if (filters?.toDate) params.append('toDate', filters.toDate);

    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request<Timeline[]>('GET', `/timelines${query}`);
  }

  async getTimeline(id: number): Promise<Timeline> {
    return this.request<Timeline>('GET', `/timelines/${id}`);
  }

  async getTimelineByDate(date: string): Promise<Timeline | null> {
    return this.request<Timeline | null>('GET', `/timelines/date/${date}`);
  }

  /** Everything physically produced on a calendar day (incl. inbound semi-finished). */
  async getProductionDay(date: string): Promise<ProductionDayResult> {
    return this.request<ProductionDayResult>('GET', `/timeline/production-day/${date}`);
  }

  // ==================== PRODUCTION WORK ORDERS ====================
  async getProductionWorkOrders(filters?: { status?: number; search?: string }): Promise<ProductionWorkOrder[]> {
    const params = new URLSearchParams();
    if (filters?.status !== undefined) params.append('status', String(filters.status));
    if (filters?.search) params.append('search', filters.search);
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request<ProductionWorkOrder[]>('GET', `/production-work-orders${query}`);
  }

  async getProductionWorkOrderStatuses(): Promise<ProductionWorkOrderStatus[]> {
    return this.request<ProductionWorkOrderStatus[]>('GET', '/production-work-order-statuses');
  }

  async getProductionWorkOrder(id: number): Promise<ProductionWorkOrder> {
    return this.request<ProductionWorkOrder>('GET', `/production-work-orders/${id}`);
  }

  /**
   * Zamjesi koji izvršavaju nalog (isti proizvod, raspon datuma oko roka) s
   * njihovim procesnim parametrima. Bez raspona backend uzima rok ±7 dana.
   */
  async getProductionWorkOrderBatches(
    id: number,
    range?: { fromDate?: string; toDate?: string },
  ): Promise<ProductionWorkOrderBatch[]> {
    const params = new URLSearchParams();
    if (range?.fromDate) params.append('fromDate', range.fromDate);
    if (range?.toDate) params.append('toDate', range.toDate);
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request<ProductionWorkOrderBatch[]>('GET', `/production-work-orders/${id}/batches${query}`);
  }

  async createProductionWorkOrder(data: ProductionWorkOrderPayload): Promise<ProductionWorkOrder> {
    return this.request<ProductionWorkOrder>('POST', '/production-work-orders', data);
  }

  async updateProductionWorkOrder(
    id: number,
    data: ProductionWorkOrderPayload,
  ): Promise<ProductionWorkOrder> {
    return this.request<ProductionWorkOrder>('PUT', `/production-work-orders/${id}`, data);
  }

  async createTimeline(data: {
    ProductionDate: string;
    batches: Partial<TimelineBatch>[];
    Notes?: string;
    /** Start of the production day for this plan ("HH:MM"); omit for the global default. */
    ShiftStartTime?: string | null;
  }): Promise<Timeline> {
    return this.request<Timeline>('POST', '/timelines', data);
  }

  // ==================== MES SETTINGS ====================
  /** Scheduling parameters (start of the production day, gap between batches, ...). */
  async getMesSettings(): Promise<MesSetting[]> {
    return this.request<MesSetting[]>('GET', '/settings');
  }

  async updateMesSettings(values: Record<string, string>): Promise<MesSetting[]> {
    return this.request<MesSetting[]>('PUT', '/settings', { values });
  }

  /** Pin the start of day for one plan; null clears it back to the global value. */
  async setTimelineShiftStart(id: number, shiftStartTime: string | null): Promise<Timeline> {
    return this.request<Timeline>('PUT', `/timelines/${id}/shift-start`, { shift_start_time: shiftStartTime });
  }

  async confirmTimeline(id: number, notes?: string): Promise<Timeline> {
    return this.request<Timeline>('POST', `/timelines/${id}/confirm`, { notes });
  }

  async createTimelineRevision(
    id: number,
    reason?: string,
    batches?: Partial<TimelineBatch>[],
  ): Promise<Timeline> {
    return this.request<Timeline>('POST', `/timelines/${id}/revision`, { reason, batches });
  }

  async reorderBatches(id: number, batchOrder: { batch_id: number; redoslijed: number }[]): Promise<Timeline> {
    return this.request<Timeline>('PUT', `/timelines/${id}/reorder`, { batch_order: batchOrder });
  }

  /** Operational reorder of a confirmed timeline (active batches only, mandatory reason, audited). */
  async operationalReorder(
    id: number,
    batchOrder: { batch_id: number; redoslijed: number }[],
    reason: string,
  ): Promise<Timeline> {
    return this.request<Timeline>('PUT', `/timelines/${id}/operational-reorder`, {
      batch_order: batchOrder,
      reason,
    });
  }

  // ==================== BATCHES ====================
  async getTimelineBatches(timelineId: number): Promise<TimelineBatch[]> {
    return this.request<TimelineBatch[]>('GET', `/timelines/${timelineId}/batches`);
  }

  async getBatches(filters?: { fromDate?: string; toDate?: string; status?: string; search?: string }): Promise<BatchListItem[]> {
    const params = new URLSearchParams();
    if (filters?.fromDate) params.append('fromDate', filters.fromDate);
    if (filters?.toDate) params.append('toDate', filters.toDate);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.search) params.append('search', filters.search);
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request<BatchListItem[]>('GET', `/batches${query}`);
  }

  async getBatch(batchId: number): Promise<BatchPassport> {
    return this.request<BatchPassport>('GET', `/batches/${batchId}`);
  }

  async updateBatchStatus(batchId: number, status: string, source?: string): Promise<TimelineBatch> {
    return this.request<TimelineBatch>('PUT', `/batches/${batchId}/status`, { status, source });
  }

  async logDispatchOverride(data: DispatchOverridePayload): Promise<any> {
    return this.request<any>('POST', '/dispatch-overrides', data);
  }

  // ==================== PROCESS PARAMETERS ====================
  async getProcessParameters(filters?: { production_date?: string; product_id?: number; id_zamjesa?: string }): Promise<ProcessParameter[]> {
    const params = new URLSearchParams();
    if (filters?.production_date) params.append('production_date', filters.production_date);
    if (filters?.product_id) params.append('product_id', String(filters.product_id));
    if (filters?.id_zamjesa) params.append('id_zamjesa', filters.id_zamjesa);

    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request<ProcessParameter[]>('GET', `/process-parameters${query}`);
  }

  async createProcessParameter(data: Partial<ProcessParameter>): Promise<ProcessParameter> {
    return this.request<ProcessParameter>(
      'POST',
      '/process-parameters',
      serializeProcessParameterForApi(data as Record<string, unknown>)
    );
  }

  async updateProcessParameter(id: number, data: Partial<ProcessParameter>): Promise<ProcessParameter> {
    return this.request<ProcessParameter>(
      'PUT',
      `/process-parameters/${id}`,
      serializeProcessParameterForApi(data as Record<string, unknown>)
    );
  }

  // ==================== BATCH ADJUSTMENTS ====================
  async getBatchAdjustments(batchId: number): Promise<BatchAdjustment[]> {
    return this.request<BatchAdjustment[]>('GET', `/batch-adjustments/${batchId}`);
  }

  /**
   * Store one ingredient deviation of a batch. Keyed on (batch, ingredient), so
   * re-saving overwrites; a deviation of zero with no reason removes the entry.
   */
  async saveBatchAdjustment(data: BatchAdjustmentInput): Promise<BatchAdjustment | null> {
    return this.request<BatchAdjustment | null>('POST', '/batch-adjustments', data);
  }

  // ==================== FLOUR LOTS & ANALYSES ====================
  async getFlourLots(filters?: { is_active?: boolean; supplier?: string }): Promise<FlourLot[]> {
    const params = new URLSearchParams();
    if (filters?.is_active !== undefined) params.append('is_active', String(filters.is_active));
    if (filters?.supplier) params.append('supplier', filters.supplier);

    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request<FlourLot[]>('GET', `/flour-lots${query}`);
  }

  async getFlourLot(id: number): Promise<FlourLot> {
    return this.request<FlourLot>('GET', `/flour-lots/${id}`);
  }

  async createFlourLot(data: Partial<FlourLot>): Promise<FlourLot> {
    return this.request<FlourLot>('POST', '/flour-lots', data);
  }

  async updateFlourLot(id: number, data: Partial<FlourLot>): Promise<FlourLot> {
    return this.request<FlourLot>('PUT', `/flour-lots/${id}`, data);
  }

  async getFlourAnalyses(lotId: number): Promise<FlourAnalysis[]> {
    return this.request<FlourAnalysis[]>('GET', `/flour-lots/${lotId}/analyses`);
  }

  async createFlourAnalysis(data: Partial<FlourAnalysis>): Promise<FlourAnalysis> {
    return this.request<FlourAnalysis>('POST', '/flour-analyses', data);
  }

  // ==================== FLOUR QC (material lots + blends) ====================
  async getFlourQualityQueue(): Promise<FlourQualityQueueItem[]> {
    return this.request<FlourQualityQueueItem[]>('GET', '/quality/flour-queue');
  }

  async getFlourQualityLotDetail(lotId: number): Promise<any> {
    return this.request<any>('GET', `/quality/flour-lots/${lotId}`);
  }

  async getFlourLotAnalysis(lotId: number): Promise<FlourLotAnalysis> {
    return this.request<FlourLotAnalysis>('GET', `/quality/flour-lots/${lotId}/analysis`);
  }

  async getFlourTestHistory(filters?: {
    status?: 'released' | 'rejected';
    limit?: number;
  }): Promise<FlourTestHistoryItem[]> {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.limit) params.append('limit', String(filters.limit));
    const q = params.toString() ? `?${params.toString()}` : '';
    return this.request<FlourTestHistoryItem[]>('GET', `/quality/flour-history${q}`);
  }

  async releaseFlourQualityLot(
    lotId: number,
    decidedBy: string,
    notes?: string,
    requireValidMeasurements?: boolean
  ): Promise<any> {
    return this.request<any>('POST', `/quality/flour-lots/${lotId}/release`, {
      decided_by: decidedBy,
      notes,
      require_valid_measurements: !!requireValidMeasurements,
    });
  }

  async rejectFlourQualityLot(lotId: number, decidedBy: string, notes?: string): Promise<any> {
    return this.request<any>('POST', `/quality/flour-lots/${lotId}/reject`, {
      decided_by: decidedBy,
      notes,
    });
  }

  // ==================== LABOS ====================
  async getLabosTrialsSelect(): Promise<LabosTrialOption[]> {
    return this.request<LabosTrialOption[]>('GET', '/labos/trials-select');
  }

  async getLabosMeasurements(
    device: LabosDeviceKey,
    opts?: { days?: number; showInvalid?: boolean; search?: string }
  ): Promise<LabosMeasurementListItem[]> {
    const params = new URLSearchParams();
    if (opts?.days) params.append('days', String(opts.days));
    if (opts?.showInvalid === false) params.append('showInvalid', 'false');
    if (opts?.search) params.append('search', opts.search);
    const q = params.toString() ? `?${params.toString()}` : '';
    return this.request<LabosMeasurementListItem[]>('GET', `/labos/measurements/${device}${q}`);
  }

  async getLabosMeasurementDetail(device: LabosDeviceKey, id: number): Promise<LabosMeasurementDetail> {
    return this.request<LabosMeasurementDetail>('GET', `/labos/measurements/${device}/${id}`);
  }

  async linkLabosMeasurement(
    device: LabosDeviceKey,
    measurementId: number,
    trialId: number,
    linkedBy?: string,
    notes?: string
  ): Promise<LabosMeasurementDetail> {
    return this.request<LabosMeasurementDetail>('POST', `/labos/measurements/${device}/${measurementId}/link`, {
      trial_id: trialId,
      linked_by: linkedBy,
      notes,
    });
  }

  async unlinkLabosTrialLink(linkId: number): Promise<void> {
    await this.request('DELETE', `/labos/trial-links/${linkId}`);
  }

  async previewLabosScale(uredaj: LabosUredaj, ingredients: LabosScaleIngredientInput[]): Promise<LabosScalePreview> {
    return this.request<LabosScalePreview>('POST', '/labos/scale-ingredients', {
      uredaj,
      ingredients,
    });
  }

  async scaleLabosFromTrial(trialId: number, uredaj: LabosUredaj): Promise<LabosScalePreview> {
    return this.request<LabosScalePreview>('POST', '/labos/scale-from-trial', { trial_id: trialId, uredaj });
  }

  async getLabosNalozi(filters?: {
    status?: string;
    uredaj?: string;
    days?: number;
  }): Promise<LabosNalog[]> {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.uredaj) params.append('uredaj', filters.uredaj);
    if (filters?.days) params.append('days', String(filters.days));
    const q = params.toString() ? `?${params.toString()}` : '';
    return this.request<LabosNalog[]>('GET', `/labos/nalozi${q}`);
  }

  async getLabosNalog(id: number): Promise<LabosNalog> {
    return this.request<LabosNalog>('GET', `/labos/nalozi/${id}`);
  }

  async createLabosNalog(data: CreateLabosNalogPayload): Promise<LabosNalog> {
    return this.request<LabosNalog>('POST', '/labos/nalozi', data);
  }

  async updateLabosNalogStatus(id: number, status: LabosNalogStatus): Promise<LabosNalog> {
    return this.request<LabosNalog>('PATCH', `/labos/nalozi/${id}/status`, { status });
  }

  // ==================== TRIALS ====================
  async getTrials(filters?: TrialFilters): Promise<Trial[]> {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.product_id) params.append('product_id', String(filters.product_id));
    if (filters?.fromDate) params.append('fromDate', filters.fromDate);
    if (filters?.toDate) params.append('toDate', filters.toDate);

    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request<Trial[]>('GET', `/trials${query}`);
  }

  async getTrial(id: number): Promise<Trial> {
    return this.request<Trial>('GET', `/trials/${id}`);
  }

  async createTrial(data: Partial<Trial> & { ingredients?: Partial<TrialIngredient>[] }): Promise<Trial> {
    return this.request<Trial>('POST', '/trials', data);
  }

  async updateTrial(id: number, data: Partial<Trial>): Promise<Trial> {
    return this.request<Trial>('PUT', `/trials/${id}`, data);
  }

  async confirmTrial(id: number, confirmedBy?: string): Promise<Trial> {
    return this.request<Trial>('POST', `/trials/${id}/confirm`, { confirmed_by: confirmedBy });
  }

  async finalizeTrial(id: number, finalScore: number): Promise<Trial> {
    return this.request<Trial>('POST', `/trials/${id}/finalize`, { final_score: finalScore });
  }

  async promoteTrial(id: number): Promise<{ trial: Trial; recipe: Recipe }> {
    return this.request<{ trial: Trial; recipe: Recipe }>('POST', `/trials/${id}/promote`);
  }

  async getTrialIngredients(id: number): Promise<TrialIngredient[]> {
    return this.request<TrialIngredient[]>('GET', `/trials/${id}/ingredients`);
  }

  async updateTrialIngredients(id: number, ingredients: Partial<TrialIngredient>[]): Promise<TrialIngredient[]> {
    return this.request<TrialIngredient[]>('PUT', `/trials/${id}/ingredients`, { ingredients });
  }

  async getTrialParameters(id: number): Promise<TrialParameter[]> {
    return this.request<TrialParameter[]>('GET', `/trials/${id}/parameters`);
  }

  async createTrialParameter(data: Partial<TrialParameter>): Promise<TrialParameter> {
    return this.request<TrialParameter>('POST', '/trial-parameters', data);
  }

  // ==================== TRIAL SCHEDULE ====================
  async getTrialSchedule(dateRange?: DateRange): Promise<TrialSchedule[]> {
    const params = new URLSearchParams();
    if (dateRange?.fromDate) params.append('fromDate', dateRange.fromDate);
    if (dateRange?.toDate) params.append('toDate', dateRange.toDate);

    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request<TrialSchedule[]>('GET', `/trial-schedule${query}`);
  }

  async updateTrialSchedule(id: number, data: Partial<TrialSchedule>): Promise<TrialSchedule> {
    return this.request<TrialSchedule>('PUT', `/trial-schedule/${id}`, data);
  }

  // ==================== DASHBOARD ====================
  async getDashboardStatus(date?: string): Promise<DashboardStatus> {
    const query = date ? `?date=${date}` : '';
    return this.request<DashboardStatus>('GET', `/dashboard/status${query}`);
  }

  // ==================== KPI ====================
  async getGanttData(dateRange: DateRange): Promise<GanttData[]> {
    const params = new URLSearchParams();
    params.append('fromDate', dateRange.fromDate);
    params.append('toDate', dateRange.toDate);
    return this.request<GanttData[]>('GET', `/kpi/timeline-gantt?${params.toString()}`);
  }

  async getMachineEfficiency(dateRange: DateRange): Promise<MachineEfficiency[]> {
    const params = new URLSearchParams();
    params.append('fromDate', dateRange.fromDate);
    params.append('toDate', dateRange.toDate);
    return this.request<MachineEfficiency[]>('GET', `/kpi/machine-efficiency?${params.toString()}`);
  }

  async getQualityTrends(dateRange: DateRange): Promise<QualityTrend[]> {
    const params = new URLSearchParams();
    params.append('fromDate', dateRange.fromDate);
    params.append('toDate', dateRange.toDate);
    return this.request<QualityTrend[]>('GET', `/kpi/quality-trends?${params.toString()}`);
  }

  async getCostAnalysis(dateRange: DateRange): Promise<any> {
    const params = new URLSearchParams();
    params.append('fromDate', dateRange.fromDate);
    params.append('toDate', dateRange.toDate);
    return this.request<any>('GET', `/kpi/costs?${params.toString()}`);
  }

  // ==================== REPORTS (IZVJEŠTAJI) ====================
  /**
   * Dnevni financijsko-operativni izvještaj: konačni plan, ostvarenje iz
   * procesnih podataka i odstupanja po proizvodu. Sve je već agregirano na
   * backendu kako bi ekran i ispis crtali istu brojku.
   */
  async getDailyProductionReport(date: string): Promise<DailyProductionReport> {
    return this.request<DailyProductionReport>('GET', `/reports/daily-production?date=${date}`);
  }

  // ==================== UTILITIES ====================
  async generateTimeline(
    dailyPlan: DailyPlanEntry[],
    modifications?: any[],
    productionDate?: string,
    /** Start of the production day ("HH:MM"); omit to use the plan's or the global setting. */
    shiftStartTime?: string | null,
  ): Promise<{ batches: Partial<TimelineBatch>[]; modifications: any[] }> {
    return this.request<{ batches: Partial<TimelineBatch>[]; modifications: any[] }>('POST', '/timeline/generate', {
      daily_plan: dailyPlan,
      modifications,
      production_date: productionDate,
      shift_start_time: shiftStartTime,
    });
  }

  /**
   * Schedule a batch list on the server without saving it — the planning
   * screen's preview after a reorder or a mass edit. Runs the same engine as
   * the save path (and accounts for other plans' batches on those days), so the
   * preview matches what will be stored.
   */
  async scheduleTimelinePreview(params: {
    batches: Partial<TimelineBatch>[];
    productionDate: string;
    shiftStartTime?: string | null;
    excludeTimelineId?: number | null;
  }): Promise<Partial<TimelineBatch>[]> {
    return this.request<Partial<TimelineBatch>[]>('POST', '/timeline/schedule-preview', {
      batches: params.batches,
      production_date: params.productionDate,
      shift_start_time: params.shiftStartTime ?? null,
      exclude_timeline_id: params.excludeTimelineId ?? null,
    });
  }

  // ==================== INGREDIENT LOTS ====================
  /** `ingredient_ids` fetches lots for several ingredients in one round trip. */
  async getIngredientLots(filters?: {
    is_active?: boolean;
    ingredient_id?: number;
    ingredient_ids?: number[];
    /** Lots of phase materials (packaging / repro), which hang off an alias only. */
    alias_ids?: number[];
    supplier?: string;
    /**
     * `production` adds `stock_by_warehouse` per lot: how much sits at the bakery
     * versus the central warehouse, in the order the two should be consumed.
     */
    stock_scope?: 'production';
  }): Promise<any> {
    const params = new URLSearchParams();
    if (filters?.is_active !== undefined) params.append('is_active', String(filters.is_active));
    if (filters?.ingredient_id) params.append('ingredient_id', String(filters.ingredient_id));
    if (filters?.ingredient_ids?.length) params.append('ingredient_ids', filters.ingredient_ids.join(','));
    if (filters?.alias_ids?.length) params.append('alias_ids', filters.alias_ids.join(','));
    if (filters?.supplier) params.append('supplier', filters.supplier);
    if (filters?.stock_scope) params.append('stock_scope', filters.stock_scope);

    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request<any>('GET', `/ingredient-lots${query}`);
  }

  async getIngredientLot(id: number): Promise<any> {
    return this.request<any>('GET', `/ingredient-lots/${id}`);
  }

  async createIngredientLot(data: any): Promise<any> {
    return this.request<any>('POST', '/ingredient-lots', data);
  }

  async updateIngredientLot(id: number, data: any): Promise<any> {
    return this.request<any>('PUT', `/ingredient-lots/${id}`, data);
  }

  async getLotUsage(lotId: number): Promise<any> {
    return this.request<any>('GET', `/ingredient-lots/${lotId}/usage`);
  }

  async createBatchLotUsage(data: any): Promise<any> {
    return this.request<any>('POST', '/batch-lot-usage', data);
  }

  /**
   * Persist planned lot assignments for a single batch (lot consumption).
   * Phase materials must carry `source_phase_material_id`, otherwise they are
   * indistinguishable from an off-recipe raw material further down the line.
   */
  async createBatchLotConsumption(
    batchId: number,
    assignments: {
      lot_id: number;
      ingredient_id: number | null;
      planned_kg: number;
      source?: string;
      ingredient_alias_id?: number | null;
      source_phase_material_id?: number | null;
    }[]
  ): Promise<any> {
    return this.request<any>('POST', `/batches/${batchId}/lot-consumption`, { assignments });
  }

  async getBatchLotUsage(batchId: number): Promise<any> {
    return this.request<any>('GET', `/batches/${batchId}/lot-usage`);
  }

  // ==================== FINISHED GOODS STOCK (GOTOVI PROIZVODI) ====================
  /** Produced finished goods available in the warehouse, aggregated per product. */
  async getFinishedGoodsStock(filters?: {
    date_from?: string;
    date_to?: string;
  }): Promise<FinishedGoodsStockOverview[]> {
    const params = new URLSearchParams();
    if (filters?.date_from) params.append('date_from', filters.date_from);
    if (filters?.date_to) params.append('date_to', filters.date_to);
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request<FinishedGoodsStockOverview[]>('GET', `/finished-goods-stock${query}`);
  }

  // ==================== TRACEABILITY (SLJEDIVOST) ====================
  async traceMaterialLots(): Promise<TraceMaterialLot[]> {
    return this.request<TraceMaterialLot[]>('GET', '/traceability/material-lots');
  }

  async traceLotForward(lotId: number): Promise<LotForwardTrace> {
    return this.request<LotForwardTrace>('GET', `/traceability/material-lots/${lotId}`);
  }

  async traceProducedLots(opts?: { include_hidden?: boolean }): Promise<TraceProducedLot[]> {
    const q = opts?.include_hidden ? '?include_hidden=1' : '';
    return this.request<TraceProducedLot[]>('GET', `/traceability/produced-lots${q}`);
  }

  async traceBatchBackward(batchId: number): Promise<BatchBackwardTrace> {
    return this.request<BatchBackwardTrace>('GET', `/traceability/batches/${batchId}`);
  }

  async traceBatchReport(batchId: number): Promise<import('../domain/MES/Warehouse/traceabilityReport').TraceabilityReport> {
    return this.request('GET', `/traceability/batches/${batchId}/report`);
  }

  async traceBatchDocumentation(batchId: number): Promise<import('../domain/MES/Warehouse/documentationPack').DocumentationPack> {
    return this.request('GET', `/traceability/batches/${batchId}/documentation`);
  }

  async traceNodeLot(lotId: number): Promise<TraceLotNodeDetail> {
    return this.request<TraceLotNodeDetail>('GET', `/traceability/node/lot/${lotId}`);
  }

  async traceNodeBatch(batchId: number): Promise<TraceBatchNodeDetail> {
    return this.request<TraceBatchNodeDetail>('GET', `/traceability/node/batch/${batchId}`);
  }

  async traceNodeDispatch(dispatchId: number): Promise<TraceDispatchNodeDetail> {
    return this.request<TraceDispatchNodeDetail>('GET', `/traceability/node/dispatch/${dispatchId}`);
  }

  async traceNodeReceipt(receiptId: number): Promise<TraceReceiptNodeDetail> {
    return this.request<TraceReceiptNodeDetail>('GET', `/traceability/node/receipt/${receiptId}`);
  }

  async traceNodeTransfer(transferId: number): Promise<TraceTransferNodeDetail> {
    return this.request<TraceTransferNodeDetail>('GET', `/traceability/node/transfer/${transferId}`);
  }

  async traceNodeProductQa(batchId: number): Promise<import('../types/mes').TraceProductQaNodeDetail> {
    return this.request('GET', `/traceability/node/product-qa/${batchId}`);
  }

  // ==================== DISPATCH NOTES (OTPREMNICE) ====================
  async getDispatchCustomers(search?: string): Promise<DispatchCustomer[]> {
    const query = search ? `?search=${encodeURIComponent(search)}` : '';
    return this.request<DispatchCustomer[]>('GET', `/customers${query}`);
  }

  async getDispatchNotes(filters?: { customer_id?: number }): Promise<DispatchNote[]> {
    const query = filters?.customer_id ? `?customer_id=${filters.customer_id}` : '';
    return this.request<DispatchNote[]>('GET', `/dispatch-notes${query}`);
  }

  async getDispatchNote(id: number): Promise<DispatchNote> {
    return this.request<DispatchNote>('GET', `/dispatch-notes/${id}`);
  }

  async createDispatchNote(payload: DispatchNotePayload): Promise<DispatchNote> {
    return this.request<DispatchNote>('POST', '/dispatch-notes', payload);
  }

  // ==================== STAFF ALLOCATION ====================
  async getStaffConfigs(filters?: { product_id?: number; machine_id?: number }): Promise<any> {
    const params = new URLSearchParams();
    if (filters?.product_id) params.append('product_id', String(filters.product_id));
    if (filters?.machine_id) params.append('machine_id', String(filters.machine_id));

    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request<any>('GET', `/staff-config${query}`);
  }

  async createStaffConfig(data: any): Promise<any> {
    return this.request<any>('POST', '/staff-config', data);
  }

  async getDailyStaff(filters: { date: string; machine_id?: number }): Promise<any> {
    const params = new URLSearchParams();
    params.append('date', filters.date);
    if (filters?.machine_id) params.append('machine_id', String(filters.machine_id));

    return this.request<any>('GET', `/staff-daily?${params.toString()}`);
  }

  async createDailyStaff(data: any): Promise<any> {
    return this.request<any>('POST', '/staff-daily', data);
  }

  async deleteDailyStaff(id: number): Promise<any> {
    return this.request<any>('DELETE', `/staff-daily/${id}`);
  }

  // ==================== MATERIAL LOTS (WAREHOUSE) ====================
  async getMaterialLots(filters?: { status?: string; ingredient_id?: number; supplier?: string }): Promise<any> {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.ingredient_id) params.append('ingredient_id', String(filters.ingredient_id));
    if (filters?.supplier) params.append('supplier', filters.supplier);

    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request<any>('GET', `/material-lots${query}`);
  }

  async getMaterialLot(id: number): Promise<any> {
    return this.request<any>('GET', `/material-lots/${id}`);
  }

  async createMaterialLot(data: any): Promise<any> {
    return this.request<any>('POST', '/material-lots', data);
  }

  async updateMaterialLot(id: number, data: any): Promise<any> {
    return this.request<any>('PUT', `/material-lots/${id}`, data);
  }

  async releaseMaterialLot(lotId: number, releasedBy: string, notes?: string): Promise<any> {
    return this.request<any>('POST', `/material-lots/${lotId}/release`, { released_by: releasedBy, notes });
  }

  // ==================== PRIMKE (bivše delivery-receipts) ====================
  async getPrimke(): Promise<any> {
    return this.request<any>('GET', '/primke');
  }

  async getGoodsReceipts(): Promise<any> {
    return this.getPrimke();
  }

  async parseDeliveryNote(imageBase64: string): Promise<any> {
    return this.request<any>('POST', '/primke/parse', { image_base64: imageBase64 });
  }

  async parseLotLabel(imageBase64: string): Promise<any> {
    return this.request<any>('POST', '/primke/parse-lot-label', { image_base64: imageBase64 });
  }

  async createPrimka(data: any): Promise<any> {
    return this.request<any>('POST', '/primke', data);
  }

  async createGoodsReceipt(data: any): Promise<any> {
    return this.createPrimka(data);
  }

  // ==================== SKLADIŠTA ====================
  /**
   * Endpoint vraća sirove Prisma retke (ID/Name/Code) — mapiraj ih na Warehouse.
   * `type` filtrira po vrsti skladišta (npr. "materijalno" za primke).
   */
  async getSkladista(filters?: { type?: string }): Promise<Warehouse[]> {
    const query = filters?.type ? `?type=${encodeURIComponent(filters.type)}` : '';
    const rows = await this.request<any[]>('GET', `/skladista${query}`);
    return (rows || []).map((w: any) => ({
      warehouse_id: w.warehouse_id ?? w.ID,
      code: w.code ?? w.Code,
      name: w.name ?? w.Name,
      type: w.type ?? w.Type,
      is_active: w.is_active ?? w.IsActive,
      sort_order: w.sort_order ?? w.SortOrder,
      is_default_receipt: w.is_default_receipt ?? w.IsDefaultReceipt,
      is_default_transfer_source:
        w.is_default_transfer_source ?? w.IsDefaultTransferSource,
      is_default_transfer_target:
        w.is_default_transfer_target ?? w.IsDefaultTransferTarget,
    }));
  }

  async getSkladistePregled(filters?: {
    warehouse_id?: number;
    ingredient_id?: number;
  }): Promise<any> {
    const params = new URLSearchParams();
    if (filters?.warehouse_id != null) {
      params.append('warehouse_id', String(filters.warehouse_id));
    }
    if (filters?.ingredient_id != null) {
      params.append('ingredient_id', String(filters.ingredient_id));
    }
    const q = params.toString() ? `?${params.toString()}` : '';
    return this.request<any>('GET', `/skladiste/pregled${q}`);
  }

  // ==================== INVENTURA ====================
  async getInventoryCounts(filters?: {
    warehouse_id?: number;
    status?: string;
    from_date?: string;
    to_date?: string;
  }): Promise<InventoryCount[]> {
    const params = new URLSearchParams();
    if (filters?.warehouse_id != null) params.append('warehouse_id', String(filters.warehouse_id));
    if (filters?.status) params.append('status', filters.status);
    if (filters?.from_date) params.append('from_date', filters.from_date);
    if (filters?.to_date) params.append('to_date', filters.to_date);
    const q = params.toString() ? `?${params.toString()}` : '';
    return this.request<InventoryCount[]>('GET', `/inventory-counts${q}`);
  }

  async getInventoryCount(id: number): Promise<InventoryCountDetail> {
    return this.request<InventoryCountDetail>('GET', `/inventory-counts/${id}`);
  }

  /** Sve što se može popisati (sirovine + ambalaža/repro) — za ručno dodavanje stavke. */
  async getCountableItems(search?: string): Promise<CountableItem[]> {
    const q = search ? `?search=${encodeURIComponent(search)}` : '';
    return this.request<CountableItem[]>('GET', `/inventory-counts/items${q}`);
  }

  /** Šifrarnici statusa, vrste popisa i razloga odstupanja. */
  async getInventoryCountLookups(): Promise<InventoryCountLookups> {
    return this.request<InventoryCountLookups>('GET', '/inventory-counts/lookups');
  }

  async createInventoryCount(data: {
    warehouse_id: number;
    count_date?: string;
    notes?: string;
    counted_by?: number;
    type_code?: string;
    include_catalogue?: boolean;
  }): Promise<InventoryCountDetail> {
    return this.request<InventoryCountDetail>('POST', '/inventory-counts', data);
  }

  async updateInventoryCount(
    id: number,
    data: { notes?: string; counted_by?: number; count_date?: string; status?: string }
  ): Promise<InventoryCountDetail> {
    return this.request<InventoryCountDetail>('PATCH', `/inventory-counts/${id}`, data);
  }

  /** Spremi popisane količine za više redaka odjednom. */
  async saveInventoryCountLines(
    id: number,
    lines: Array<{
      line_id: number;
      counted_quantity_kg?: number | null;
      notes?: string;
      reason_id?: number | null;
      lot_number?: string;
      expiry_date?: string;
    }>
  ): Promise<InventoryCountDetail> {
    return this.request<InventoryCountDetail>('PUT', `/inventory-counts/${id}/lines`, { lines });
  }

  async addInventoryCountLine(
    id: number,
    data: {
      ingredient_id?: number;
      ingredient_alias_id?: number;
      material_name?: string;
      lot_number?: string;
      expiry_date?: string;
      counted_quantity_kg?: number | null;
      unit_price?: number;
      reason_id?: number;
      notes?: string;
    }
  ): Promise<InventoryCountDetail> {
    return this.request<InventoryCountDetail>('POST', `/inventory-counts/${id}/lines`, data);
  }

  async removeInventoryCountLine(id: number, lineId: number): Promise<InventoryCountDetail> {
    return this.request<InventoryCountDetail>('DELETE', `/inventory-counts/${id}/lines/${lineId}`);
  }

  /** Knjiži razlike kao usklađenje zalihe — dokument postaje nepromjenjiv. */
  async postInventoryCount(id: number, performedBy?: number): Promise<InventoryCountDetail> {
    return this.request<InventoryCountDetail>('POST', `/inventory-counts/${id}/post`, {
      performed_by: performedBy,
    });
  }

  async cancelInventoryCount(id: number): Promise<InventoryCountDetail> {
    return this.request<InventoryCountDetail>('POST', `/inventory-counts/${id}/cancel`, {});
  }

  // ==================== INTER-WAREHOUSE TRANSFERS ====================
  async getInterWarehouseTransfers(filters?: {
    status?: string;
    from_date?: string;
    to_date?: string;
  }): Promise<any> {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.from_date) params.append('from_date', filters.from_date);
    if (filters?.to_date) params.append('to_date', filters.to_date);
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request<any>('GET', `/inter-warehouse-transfers${query}`);
  }

  async getInterWarehouseTransfer(id: number): Promise<any> {
    return this.request<any>('GET', `/inter-warehouse-transfers/${id}`);
  }

  /** Bez šifre skladišta vrijedi ono koje poslužitelj koristi kao izvor međuskladišnice. */
  async getTransferAvailableLots(warehouseCode?: string, search?: string): Promise<TransferAvailableLot[]> {
    const params = new URLSearchParams();
    if (warehouseCode) params.append('warehouseCode', warehouseCode);
    if (search) params.append('search', search);
    const query = params.toString();
    return this.request<TransferAvailableLot[]>(
      'GET',
      `/inter-warehouse-transfers/available-lots${query ? `?${query}` : ''}`
    );
  }

  async previewInterWarehouseTransfer(productionDate: string, timelineId?: number): Promise<any> {
    return this.request<any>('POST', '/inter-warehouse-transfers/preview', {
      production_date: productionDate,
      timeline_id: timelineId,
    });
  }

  async generateInterWarehouseTransfer(productionDate: string, timelineId?: number): Promise<any> {
    return this.request<any>('POST', '/inter-warehouse-transfers/generate', {
      production_date: productionDate,
      timeline_id: timelineId,
    });
  }

  async createInterWarehouseTransfer(data: any): Promise<any> {
    return this.request<any>('POST', '/inter-warehouse-transfers', data);
  }

  async updateInterWarehouseTransferStatus(id: number, status: string): Promise<any> {
    return this.request<any>('PATCH', `/inter-warehouse-transfers/${id}/status`, { status });
  }

  async publishInterWarehouseTransfer(id: number): Promise<any> {
    return this.request<any>('POST', `/inter-warehouse-transfers/${id}/publish`, {});
  }

  async setInterWarehouseTransferLineTransferred(
    transferId: number,
    lineId: number,
    transferred: boolean
  ): Promise<InterWarehouseTransfer> {
    return this.request<InterWarehouseTransfer>(
      'PATCH',
      `/inter-warehouse-transfers/${transferId}/lines/${lineId}`,
      { transferred }
    );
  }

  async completeInterWarehouseTransfer(id: number, performedBy?: string): Promise<any> {
    return this.request<any>('POST', `/inter-warehouse-transfers/${id}/complete`, {
      performed_by: performedBy,
    });
  }

  async getMaterialLotUsage(lotId: number): Promise<any> {
    return this.request<any>('GET', `/material-lots/${lotId}/usage`);
  }

  // ==================== SUPPLIERS ====================
  async getSuppliers(filters?: { is_active?: boolean }): Promise<any> {
    const params = new URLSearchParams();
    if (filters?.is_active !== undefined) params.append('is_active', String(filters.is_active));

    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request<any>('GET', `/suppliers${query}`);
  }

  async getSupplier(id: number): Promise<any> {
    return this.request<any>('GET', `/suppliers/${id}`);
  }

  async createSupplier(data: any): Promise<any> {
    return this.request<any>('POST', '/suppliers', data);
  }

  async updateSupplier(id: number, data: any): Promise<any> {
    return this.request<any>('PUT', `/suppliers/${id}`, data);
  }

  // ==================== STOCK SUMMARY ====================
  async getStockSummary(): Promise<any> {
    return this.request<any>('GET', '/warehouse/stock-summary');
  }

  // ==================== STAFF & SHIFTS ====================
  async getStaff(filters?: { is_active?: boolean; department?: string }): Promise<any> {
    const params = new URLSearchParams();
    if (filters?.is_active !== undefined) params.append('is_active', String(filters.is_active));
    if (filters?.department) params.append('department', filters.department);

    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request<any>('GET', `/staff${query}`);
  }

  async getStaffMember(id: number): Promise<any> {
    return this.request<any>('GET', `/staff/${id}`);
  }

  async createStaffMember(data: any): Promise<any> {
    return this.request<any>('POST', '/staff', data);
  }

  async updateStaffMember(id: number, data: any): Promise<any> {
    return this.request<any>('PUT', `/staff/${id}`, data);
  }

  async deleteStaffMember(id: number): Promise<any> {
    return this.request<any>('DELETE', `/staff/${id}`);
  }

  async addStaffSkill(staffId: number, data: any): Promise<any> {
    return this.request<any>('POST', `/staff/${staffId}/skills`, data);
  }

  async getStaffSkills(staffId: number): Promise<any> {
    return this.request<any>('GET', `/staff/${staffId}/skills`);
  }

  async getShifts(filters?: { is_active?: boolean }): Promise<any> {
    const params = new URLSearchParams();
    if (filters?.is_active !== undefined) params.append('is_active', String(filters.is_active));

    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request<any>('GET', `/shifts${query}`);
  }

  async getShift(id: number): Promise<any> {
    return this.request<any>('GET', `/shifts/${id}`);
  }

  async createShift(data: any): Promise<any> {
    return this.request<any>('POST', '/shifts', data);
  }

  async updateShift(id: number, data: any): Promise<any> {
    return this.request<any>('PUT', `/shifts/${id}`, data);
  }

  async getShiftAssignments(fromDate: string, toDate: string): Promise<any> {
    const params = new URLSearchParams();
    params.append('from_date', fromDate);
    params.append('to_date', toDate);

    return this.request<any>('GET', `/shift-assignments?${params.toString()}`);
  }

  async getShiftAssignment(id: number): Promise<any> {
    return this.request<any>('GET', `/shift-assignments/${id}`);
  }

  async createShiftAssignment(data: any): Promise<any> {
    return this.request<any>('POST', '/shift-assignments', data);
  }

  async updateShiftAssignment(id: number, data: any): Promise<any> {
    return this.request<any>('PUT', `/shift-assignments/${id}`, data);
  }

  async deleteShiftAssignment(id: number): Promise<any> {
    return this.request<any>('DELETE', `/shift-assignments/${id}`);
  }

  // ==================== PROCUREMENT: ORDER REQUESTS (Nabava) ====================
  async getOrderRequests(filters?: { status?: number; search?: string }): Promise<OrderRequest[]> {
    const params = new URLSearchParams();
    if (filters?.status !== undefined) params.append('status', String(filters.status));
    if (filters?.search) params.append('search', filters.search);

    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request<OrderRequest[]>('GET', `/order-requests${query}`);
  }

  async getOrderRequest(id: number): Promise<OrderRequest> {
    return this.request<OrderRequest>('GET', `/order-requests/${id}`);
  }

  async createOrderRequest(data: OrderRequestPayload): Promise<OrderRequest> {
    return this.request<OrderRequest>('POST', '/order-requests', data);
  }

  async getOrderRequestStatuses(): Promise<OrderRequestStatus[]> {
    return this.request<OrderRequestStatus[]>('GET', '/order-request-statuses');
  }

  async getUnitTypes(): Promise<UnitType[]> {
    return this.request<UnitType[]>('GET', '/unit-types');
  }

  /** Pogoni (Pekara / Slastičarna) za dropdown na proizvodu. */
  async getProductionUnits(): Promise<ProductionUnit[]> {
    return this.request<ProductionUnit[]>('GET', '/production-units');
  }

  /**
   * Prebacivanje recepture između pekarskog postotka i apsolutnih količina, uz
   * množitelje koje slastičarna gleda. Sprema se u mjestu, bez nove verzije —
   * osnova govori kako se brojevi čitaju, a ne mijenja same brojeve.
   */
  async setRecipeQuantityBasis(
    recipeId: number,
    data: { QuantityBasis?: RecipeQuantityBasis; ScaleFactors?: number[] | string | null },
  ): Promise<Recipe> {
    return this.request<Recipe>('PUT', `/recipes/${recipeId}/quantity-basis`, data);
  }

  // ==================== TIMELINE RECALCULATION ====================
  /** @deprecated Backend nema ovaj endpoint; koristi reorderBatches + generateTimeline */
  async recalculateTimeline(id: number): Promise<Timeline> {
    return this.getTimeline(id);
  }
}

// Export singleton instance
export const mesApi = new MESApi();
export default mesApi;
